import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import InfoPopup from './InfoPopup';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showFAQ, setShowFAQ] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const socialLinks = [
    { icon: Facebook, link: 'https://www.facebook.com/profile.php?id=61581418439811', color: 'hover:text-blue-500', label: 'Facebook' },
    { icon: Instagram, link: 'https://www.instagram.com/navaproperties/', color: 'hover:text-pink-500', label: 'Instagram' },
    { icon: Linkedin, link: 'https://www.linkedin.com/company/nava-properties-dubai/', color: 'hover:text-blue-400', label: 'LinkedIn' },
  ];

  const areas = [
    { name: 'Dubai South', id: 'dubai-south' },
    { name: 'Furjan', id: 'furjan' },
    { name: 'Wasl Gate', id: 'wasl-gate' },
    { name: 'JBR', id: 'jbr' },
    { name: 'Dubai Land', id: 'dubai-land' },
  ];

  const faqContent = (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">What areas does Nava Properties cover?</h3>
        <p className="text-gray-600 leading-relaxed">
          We specialize in prime Dubai locations including Dubai South, Furjan, Wasl Gate, JBR, and Dubai Land. Our team has deep knowledge of these high-growth areas and can guide you to the best investment opportunities.
        </p>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">How long does the property buying process take?</h3>
        <p className="text-gray-600 leading-relaxed">
          The timeline varies depending on the property type. For ready properties, the process typically takes 2-4 weeks. For off-plan properties, completion ranges from 6 months to 3 years. We guide you through every step to ensure a smooth experience.
        </p>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">Can foreigners buy property in Dubai?</h3>
        <p className="text-gray-600 leading-relaxed">
          Yes, Dubai offers freehold ownership in designated areas, allowing foreign investors to own property outright. We specialize in helping international clients navigate the process, from property selection to legal documentation.
        </p>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">What are the costs involved in buying property?</h3>
        <p className="text-gray-600 leading-relaxed">
          Beyond the property price, expect Dubai Land Department fees (4% for ready properties, 0% for off-plan), agent commission (typically 2%), mortgage registration fees if applicable, and service charges. We provide a complete breakdown before you commit.
        </p>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">What is your investment advisory approach?</h3>
        <p className="text-gray-600 leading-relaxed">
          We follow a research-driven, transparent approach. No false promises—only data-backed insights. We analyze market trends, ROI potential, rental yields, and growth projections to help you make informed decisions aligned with your financial goals.
        </p>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">Do you offer property management services?</h3>
        <p className="text-gray-600 leading-relaxed">
          While our primary focus is investment advisory and property sales, we can connect you with trusted property management partners who handle tenant screening, maintenance, and rental collection.
        </p>
      </div>
    </div>
  );

  const privacyContent = (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">Information We Collect</h3>
        <p className="text-gray-600 leading-relaxed mb-3">
          We collect information you provide directly to us when you:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
          <li>Fill out contact forms or request property information</li>
          <li>Subscribe to our newsletter or market reports</li>
          <li>Schedule consultations or property viewings</li>
          <li>Communicate with our team via email or phone</li>
        </ul>
        <p className="text-gray-600 leading-relaxed mt-3">
          This may include your name, email address, phone number, country, investment preferences, and financial goals.
        </p>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">How We Use Your Information</h3>
        <p className="text-gray-600 leading-relaxed mb-3">
          We use the information we collect to:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
          <li>Provide personalized property recommendations</li>
          <li>Send market analysis reports and investment opportunities</li>
          <li>Schedule and coordinate property viewings</li>
          <li>Answer your inquiries and provide customer support</li>
          <li>Improve our services and website experience</li>
        </ul>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">Data Protection & Security</h3>
        <p className="text-gray-600 leading-relaxed">
          We implement industry-standard security measures to protect your personal information. Your data is stored securely and is only accessible to authorized team members who need it to serve you better. We never sell or share your information with third parties for marketing purposes.
        </p>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">Your Rights</h3>
        <p className="text-gray-600 leading-relaxed mb-3">
          You have the right to:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
          <li>Access the personal data we hold about you</li>
          <li>Request corrections to any inaccurate information</li>
          <li>Request deletion of your personal data</li>
          <li>Unsubscribe from our communications at any time</li>
          <li>Object to the processing of your personal data</li>
        </ul>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-3">Contact Us</h3>
        <p className="text-gray-600 leading-relaxed">
          If you have any questions about our privacy practices or wish to exercise your rights, please contact us at sales@navaproperties.me or call +971 5267 80561.
        </p>
      </div>
      <div>
        <p className="text-gray-500 text-sm italic">
          Last updated: {currentYear}. We reserve the right to update this policy. Any changes will be posted on this page.
        </p>
      </div>
    </div>
  );

  return (
    <>
      <footer className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 border-t border-gray-200 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              x: [0, 100, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="container mx-auto px-6 py-16 relative z-10">
          {/* Main content */}
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            {/* Left side - Brand & Contact */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_d147d433-443c-4020-a063-c9d8d331dbac/artifacts/efecvbvv_Color%20logo%20-%20no%20background.svg"
                  alt="Nava Properties"
                  className="h-16 mb-6"
                />
                <p className="text-gray-600 leading-relaxed mb-6">
                  Creating wealth through strategic Dubai real estate investments. 7+ years of expertise, 100+ satisfied global investors.
                </p>

                {/* Contact info */}
                <div className="space-y-3">
                  <a href="mailto:sales@navaproperties.me" className="flex items-center gap-3 text-gray-600 hover:text-yellow-600 transition-colors">
                    <Mail className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm font-medium">sales@navaproperties.me</span>
                  </a>
                  <a href="tel:+971526780561" className="flex items-center gap-3 text-gray-600 hover:text-yellow-600 transition-colors">
                    <Phone className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm font-medium">+971 5267 80561</span>
                  </a>
                  <div className="flex items-start gap-3 text-gray-600">
                    <MapPin className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-medium">OFFICE 1 XAVIER BUSINESS CENTRE, IBN BATUTA GATE OFFICE. DUBAI</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right side - Links */}
            <div className="grid sm:grid-cols-2 gap-8">
              {/* Quick Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="text-yellow-600 font-bold text-lg mb-4">Quick Links</h3>
                <ul className="space-y-3">
                  <li>
                    <a
                      href="#team"
                      className="text-gray-600 hover:text-yellow-600 transition-colors text-sm font-medium inline-block"
                    >
                      About Us
                    </a>
                  </li>
                  <li>
                    <a
                      href="#contact"
                      className="text-gray-600 hover:text-yellow-600 transition-colors text-sm font-medium inline-block"
                    >
                      Contact
                    </a>
                  </li>
                  <li>
                    <button
                      onClick={() => setShowFAQ(true)}
                      className="text-gray-600 hover:text-yellow-600 transition-colors text-sm font-medium inline-block"
                    >
                      FAQ
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setShowPrivacy(true)}
                      className="text-gray-600 hover:text-yellow-600 transition-colors text-sm font-medium inline-block"
                    >
                      Privacy Policy
                    </button>
                  </li>
                </ul>
              </motion.div>

              {/* Areas */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="text-yellow-600 font-bold text-lg mb-4">Areas</h3>
                <ul className="space-y-3">
                  {areas.map((area) => (
                    <li key={area.id}>
                      <Link
                        to={`/area/${area.id}`}
                        className="text-gray-600 hover:text-yellow-600 transition-colors text-sm font-medium inline-block"
                      >
                        {area.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>

          {/* Newsletter section */}
          <motion.div
            className="mb-12 p-8 bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border border-yellow-400/30 rounded-3xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                Stay Updated with Dubai Market Insights
              </h3>
              <p className="text-gray-600 mb-6">
                Subscribe to receive exclusive property listings and market analysis reports
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-6 py-4 bg-white border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:border-yellow-400 transition-colors"
                />
                <motion.button
                  className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Subscribe Now
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-8" />

          {/* Bottom section */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Copyright */}
            <motion.p
              className="text-gray-500 text-sm text-center md:text-left"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              © {currentYear} Nava Properties. All rights reserved.
            </motion.p>

            {/* Social links */}
            <motion.div
              className="flex gap-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className={`w-11 h-11 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center text-gray-600 ${social.color} hover:border-yellow-400 transition-all duration-300`}
                    whileHover={{ scale: 1.1, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </motion.div>
          </div>
        </div>

        {/* Scroll to top button */}
        <motion.button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-gray-900 shadow-2xl shadow-yellow-500/50 hover:shadow-yellow-500/80 transition-all duration-300 z-40"
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: false }}
          whileHover={{ scale: 1.1, y: -5 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </motion.button>
      </footer>

      {/* Popups */}
      <InfoPopup
        isOpen={showFAQ}
        onClose={() => setShowFAQ(false)}
        title="Frequently Asked Questions"
        content={faqContent}
      />
      <InfoPopup
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
        title="Privacy Policy"
        content={privacyContent}
      />
    </>
  );
};

export default Footer;
